import { usePopoverContext } from '@chakra-ui/react';
import { Children, cloneElement, ReactNode } from 'react';

/**
 * In Short: Chakra's popover component is not accessible when using `trigger="hover"`.
 *
 * See: https://ifixit.slack.com/archives/C01GVQAAGDU/p1675881445880599
 *
 * According to the WAI-ARIA spec, a tooltip is not a popup. A tooltip is not intractable, while a popup is.
 * The problem is that Chakra is using the `aria-haspopup`, `aria-expanded` and ` aria-controls` attributes always
 * but swap the role between `tooltip` and `dialog` depending on the `trigger` prop.
 *
 * The above attributes are only valid for a `dialog` role, not for a `tooltip` role.
 *
 * Our hack fix is to mimic the `PopoverTrigger` component and remove the invalid attributes.
 * One major fault is that chakra does not allow us to access the trigger prop to check if it is "hover", so
 * this component must be opt in at each call site.
 */
export function HoverPopoverTrigger(props: { children: ReactNode }) {
   // enforce a single child
   const child: any = Children.only(props.children);
   const { getTriggerProps } = usePopoverContext();
   const triggerProps = getTriggerProps(child.props, child.ref);
   delete triggerProps['aria-haspopup'];
   delete triggerProps['aria-expanded'];
   delete triggerProps['aria-controls'];
   return cloneElement(child, triggerProps);
}
